import React, {useEffect, useMemo, useState} from "react"
import {Layout} from 'antd';
import {Link} from "react-router-dom";
import {useAuth} from "../../../providers/AuthProvider";
import ExpandableHtmlText from "../../../components/ExpandableHtmlText";
import {useLocation} from "react-use";
import {useTeam} from "../../../providers/TeamProvider";
import useBottomVisibility from "../../../hooks/useBottomVisibility";

const {Sider} = Layout;

const TeamDisplay = () => {
    const {team} = useTeam();
    const totalMembers = team.positions.length;

    // Adjust sizing based on number of members
    const getSizeClasses = () => {
        switch(totalMembers) {
            case 1:
                return "w-16 h-16"; // 64px for single member
            case 2:
                return "w-14 h-14"; // 56px for two members
            case 3:
                return "w-12 h-12"; // 48px for three members
            default:
                return "w-10 h-10"; // 40px for four or more members
        }
    };

    // Determine layout based on number of members
    const getLayout = () => {
        if (totalMembers <= 3) {
            // All members in a single row for small teams
            return {
                firstRow: team.positions,
                secondRow: []
            };
        }
        // Default layout with two rows
        return {
            firstRow: team.positions.slice(0, 4),
            secondRow: team.positions.slice(4, 7)
        };
    };

    const { firstRow, secondRow } = getLayout();
    const remainingCount = totalMembers > 7 ? totalMembers - 7 : 0;
    const showRemainingCount = remainingCount > 0;
    const sizeClasses = getSizeClasses();

    return (
        <div className="self-stretch h-40 p-3">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <i className="text-xl fa fa-users text-blue-500" />
                    <h2 className="text-xl font-semibold text-gray-900">{team.name}</h2>
                </div>
                {/*<span className="text-sm text-gray-500">{team.positions.length} members</span>*/}
            </div>

            <div className="flex gap-3">
                <div className="space-y-2">
                    {/* First row */}
                    <div className="flex -space-x-2">
                        {firstRow.map((member, index) => (
                            <div key={index} className="relative"    style={{zIndex: index + 1}}>
                                <img
                                    src={member.image_path}
                                    alt={`${member.first_name} ${member.last_name}`}

                                    className={`${sizeClasses} grayscale rounded-full border-1 border-gray-300 object-cover hover:z-10 transition-transform hover:scale-110`}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Second row - only shown if there are more than 3 members */}
                    {secondRow.length > 0 && (
                        <div className="flex -space-x-2">
                            {secondRow.map((member, index) => (
                                <div key={index} className="relative" style={{zIndex: index + 1}}>
                                    <img
                                        src={member.image_path}
                                        alt={`${member.first_name} ${member.last_name}`}

                                        className={`${sizeClasses} grayscale rounded-full border-1 border-gray-300 object-cover hover:z-10 transition-transform hover:scale-110`}
                                    />
                                </div>
                            ))}
                            {showRemainingCount && (
                                <div style={{zIndex: secondRow?.length + 2}} className={`${sizeClasses} rounded-full border-1 border-gray-300 bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600`}>
                                    +{remainingCount}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 line-clamp-2 max-w-full">
                        {team.positions.map(m => m.first_name).join(', ')}
                    </p>
                    <p className="text-xs text-gray-500">
                        {totalMembers > 7
                            ? `7 of ${totalMembers} members shown`
                            : `${totalMembers} team members`}
                    </p>
                </div>
            </div>
        </div>
    );
};

const TeamProfileDetails = ({shouldMinimizeContent, isVisible, onVisible}) => {
    const {auth} = useAuth();
    const {team} = useTeam();
    const [isBioOpen, setIsBioOpen] = useState(false);

    const location = useLocation();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const {
        containerRef,
        bottomMarkerRef,
        bottomMarkerStyle = {},
        isBottomVisible,
        wasBottomVisibleOnLoad,
        hasCheckedInitialVisibility
    } = useBottomVisibility();

    // Initialize if bottom is visible on load
    useEffect(() => {
        if (hasCheckedInitialVisibility) {
            console.log('InitLoadChange', {isVisible, wasBottomVisibleOnLoad})
            onVisible(wasBottomVisibleOnLoad)
        }
    }, [wasBottomVisibleOnLoad, hasCheckedInitialVisibility]);


    // Send changed when bottom is visible
    useEffect(() => {
        if (hasCheckedInitialVisibility && wasBottomVisibleOnLoad !== isBottomVisible) {
            console.log('AfterLoadChange', {isBottomVisible, isVisible, wasBottomVisibleOnLoad})
            onVisible(isBottomVisible)
        }
    }, [isBottomVisible]);

    // Reset bio to closed after affixed and minimized
    useEffect(() => {
        if (shouldMinimizeContent && isBioOpen) {
            setIsBioOpen(isBioOpen)
        }
    }, [shouldMinimizeContent]);


    const [backBtnTitle, redirectTo] = useMemo(() => {
        let path = location?.pathname || '';
        let title = 'Go Back';
        let redirect = {
            ...location,
            pathname: path.substring(0, path.indexOf('/team-profiles'))
        }

        console.log('redirecttt', redirect)

        if (!redirect.pathname?.length) {
            const canGoBack = window.history.length > 1 && TB.isDefined(location?.key);
            console.log('cangoback', {canGoBack})
            redirect = !canGoBack ? TB.auth().appropriate() : -1
        }
        // else if (TB.auth().InitIntended?.pathname !== location.pathname) {
        //     redirect = -1;
        // }

        return [title,redirect]
    }, [location.pathname]);

    return (
        <Sider
            trigger={null}
            collapsible
            collapsed={isSidebarCollapsed}
            width={448}
            collapsedWidth={190}
            className="bg-transparent"
        >
            <div ref={containerRef} className="bg-white pt-12 px-4 pb-6 drop-shadow-md">

                <div className="absolute left-4 top-0 flex flex-row items-center gap-2">
                    <Link to={redirectTo} className="btn btn-link text-[#323C47] hover:text-[#1A1F25] focus:text-[#1A1F25] no-underline py-0 h-12 gap-1 flex flex-row items-center">
                        <i className="fas fa-chevron-left"/>
                        <span>
                            {backBtnTitle}
                        </span>
                    </Link>
                </div>

                <div>
                    <div className="flex justify-between relative">

                        <div className={TB.mapClass("flex items-center", {
                            "gap-4": !shouldMinimizeContent && !isSidebarCollapsed,
                            "gap-2": shouldMinimizeContent || isSidebarCollapsed,
                            "mb-3": !shouldMinimizeContent,
                            "flex-col": isSidebarCollapsed,
                        })}>

                            <TeamDisplay/>

                        </div>


                        <button
                            type="button"
                            className="btn-outline bg-wrippleOffWhite h-6 w-6 ml-auto shrink-0 flex items-center justify-center"
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                        >
                            <i className={TB.mapClass("fa fa-chevron-left transition text-base", {'rotate-180': isSidebarCollapsed})}/>
                        </button>

                    </div>

                    <div className={TB.mapClass("transition-all duration-75", {'opacity-0 invisible h-0': isSidebarCollapsed})}>
                        <div
                            className={TB.mapClass("flex flex-col gap-4 transition-all delay-150", {'opacity-0 invisible': isSidebarCollapsed})}>

                            <div>

                                <div className="flex items-end gap-3">
                                    <ExpandableHtmlText
                                        shouldExpand={isBioOpen}
                                        onChange={setIsBioOpen}
                                        showReadMore={false}
                                        content={TB.stripHtml(team.bio)?.length > 0 ? team.bio : '<i>No About Provided</i>'}
                                        maxHeight={350}>
                                        <button
                                            type="button"
                                            className="btn-outline bg-wrippleOffWhite h-6 w-6 shrink-0 flex items-center justify-center"
                                            onClick={() => {
                                                const visible = !isBioOpen;
                                                setIsBioOpen(!isBioOpen)
                                                onVisible(visible)
                                            }}
                                        >
                                            <i className={TB.mapClass("fa fa-chevron-down transition text-base", {'rotate-180': isBioOpen})}/>
                                        </button>
                                    </ExpandableHtmlText>


                                </div>
                            </div>


                            {/*<div>*/}
                            {/*    <p className="text-base font-medium pb-2">Roles</p>*/}

                            {/*    <div className="flex gap-4 flex-wrap">*/}
                            {/*        /!*{TB.map(visibleTalentDisciplines, discipline => (*!/*/}
                            {/*        /!*    <div key={discipline.id}*!/*/}
                            {/*        /!*         className="text-center border border-secondary rounded-lg p-2 pb-1">*!/*/}
                            {/*        /!*        <p className="text-xs font-bold">{discipline.name}</p>*!/*/}
                            {/*        /!*        {!shouldMinimizeContent && (*!/*/}
                            {/*        /!*            <p className="text-xxs text-[#3E3F42]">{scoringOptions[discipline.self_score]}</p>*!/*/}
                            {/*        /!*        )}*!/*/}
                            {/*        /!*    </div>*!/*/}
                            {/*        /!*))}*!/*/}

                            {/*        /!*{!shouldMinimizeContent && !isRolesOpen && (talent.talent_disciplines.length - 4) > 0 && (*!/*/}
                            {/*        /!*    <button*!/*/}
                            {/*        /!*        type="button"*!/*/}
                            {/*        /!*        onClick={() => setIsRolesOpen(!isRolesOpen)}*!/*/}
                            {/*        /!*        className="text-center border border-secondary flex items-center justify-center rounded-lg py-2 px-5"*!/*/}
                            {/*        /!*    >*!/*/}
                            {/*        /!*        <p className="text-xs font-bold">+ {talent.talent_disciplines.length - 4}</p>*!/*/}
                            {/*        /!*    </button>*!/*/}
                            {/*        /!*)}*!/*/}
                            {/*        /!*{shouldMinimizeContent && (talent.talent_disciplines.length - 4) > 0 && (*!/*/}
                            {/*        /!*    <p*!/*/}
                            {/*        /!*        className="text-xxs mr-auto text-left self-center"*!/*/}
                            {/*        /!*    >*!/*/}
                            {/*        /!*        (+ {talent.talent_disciplines.length - 4} more)*!/*/}
                            {/*        /!*    </p>*!/*/}
                            {/*        /!*)}*!/*/}
                            {/*    </div>*/}
                            {/*</div>*/}

                            {team.profile_links?.length > 0 && (
                                <div>
                                    <p className="text-base font-medium pb-2">Links</p>

                                    <div className="flex flex-col items-stretch gap-1.5">
                                        {TB.map(team.profile_links, (link, index) => {
                                            if (!link.type_icon) {
                                                return null;
                                            }
                                            return (
                                                <div className="flex flex-row flex-nowrap items-center gap-1.5 overflow-x-hidden"
                                                     key={`profile-links-${index}`}>
                                                    <i className={TB.mapClass(`text-sm fa-${link.type_icon}`, {
                                                        "fab": link.type_icon.includes('fa-brand'),
                                                        "fas": !link.type_icon.includes('fa-brand')
                                                    })} style={{color: link.type_color || '#000'}}/>
                                                    <Link to={link.url} target="_blank"
                                                          className="text-xs hover:underline text-info hover:font-medium truncate flex-grow">
                                                        {link.url}
                                                </Link>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>

                            )}

                        </div>
                    </div>
                </div>

                {/* Bottom ref to check that entire content has been seen */}
                <div
                    ref={bottomMarkerRef}
                    className="absolute bottom-0 left-0 w-full h-[0.05rem]"
                    style={bottomMarkerStyle}
                />
            </div>
        </Sider>
    )
}

export default TeamProfileDetails;